import "./_internal/CasMultiRowTable.scss";

import React, { useMemo, useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Stack,
  IconButton,
  TableHead,
  StackProps,
  FormHelperText,
  Box,
} from '@mui/material';
import Iconify from 'src/components/iconify';
// hooks
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
// utils
import { getEmptyRow } from 'src/compass/shared/util/getEmptyRow';
import { useResponsive } from "src/hooks/use-responsive";
// components
import {
  CasIconButton,
  CasRHFTextField,
  CasRHFAutocomplete,
  CasRHFMultilineTextField,
  CasDisplayField,
} from 'src/compass/shared/components';
import CasMultiRowTotalsRow from './_internal/CasMultiRowTotalsRow';

/*
  A column to total in the footer row: the field name, plus rowValue when the column is
  calculated rather than stored (a "display" column has nothing in the form data to sum).
*/
export type ITotalColumn = {
  name: string;
  rowValue?: (row: any) => number;
  format?: (total: number) => string;
};

export interface IColumns {
  type: 'text' | 'select' | 'component' | 'cells' | 'multilineText' | 'display';
  label: string;
  value?:
    | string
    | number
    | JSX.Element
    | (({ rowNameRef }: { rowNameRef: string }) => string | number | JSX.Element);
  name: string;
  options?: any[];
  inputAdornment?: string;
  inputAdornmentType?: 'endAdornment' | 'startAdornment';
  inputAdornmentPosition?: 'end' | 'start';
  component?: (props: any) => JSX.Element;
  componentProps?: any;
  defaultValue?: any;
  fullWidth?: boolean;
  include?: boolean;
  clearErrorsForOtherField?: string;
  width?: React.CSSProperties['width'];
  // Cell alignment; display columns default to centre, as they always have
  align?: 'left' | 'center' | 'right';
}

export interface ICasMultiRowTable {
  readOnly?: boolean;
  title?: string;
  row1Cell1?: any;
  rowCountMode?: 0 | 1 | 2;
  /*
    useFieldArray doesn't respond to form data changes external to its methods.
    replaceData should be used when the table should be updated after an external change.
    NOTES:
      - replaceData uses replace - which causes remount - which loses cursor position
      - The problem isn't useFieldArray. (Try using a TextField instead of CasAutocomplete - and it works)
       It appears to be either 
          (a) Bug in Autocomplete?
          (b) CasAutocomplete implem
  */
  replaceData?: any;
  columns: IColumns[];
  name: string;
  showColumnHeaders?: boolean;
  alignItems?: StackProps['alignItems'];
  width?: React.CSSProperties['width'];
  helperText?: string;
  onRowDelete?: (index: number) => void;
  /*
    The rows come from data, not from the user - hide the add and delete buttons while leaving
    the inputs editable.  readOnly can't serve this: it disables the inputs too.
  */
  fixedRows?: boolean;
  // Columns to sum into a footer row, aligned under their own column
  totalColumns?: ITotalColumn[];
}

const CasMultiRowTable = ({
  readOnly = false,
  fixedRows = false,
  totalColumns,
  title,
  row1Cell1,
  name,
  rowCountMode = 0,
  columns: parmColumns,
  replaceData,
  showColumnHeaders = true,
  alignItems = 'center',
  width = 'auto',
  helperText,
  onRowDelete,
}: ICasMultiRowTable) => {
  const includedColumns = parmColumns.filter((col) => col.include !== false);

  const [isEmptyRowAdded, setIsEmptyRowAdded] = useState<boolean>(false);
  const { setValue, control, clearErrors } = useFormContext();
  const smDown = useResponsive('down', 'sm');

  const { fields, append, remove, replace } = useFieldArray({ control, name });
  /*
    Note: useFieldArray assigns its own generated unique key
      to each row's "id" property
  */

  // eslint-disable-next-line react-hooks/exhaustive-deps 
  const emptyRow = useMemo(() => getEmptyRow(includedColumns), []);

  useEffect(() => {
    if (replaceData !== undefined) replace(replaceData);
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [replaceData]);

  useEffect(() => {
    if (!isEmptyRowAdded && rowCountMode > 0 && fields.length < rowCountMode) {
      const rowCountToAdd = rowCountMode - fields.length;

      Array.from({ length: rowCountToAdd }).forEach(() => {
        append(emptyRow);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [fields, append]);

  useEffect(() => {
    if (fields.length > 0 && rowCountMode === 2 && row1Cell1)
      setValue(`${name}[0].${includedColumns[0].name}`, row1Cell1);
  // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [fields, row1Cell1, setValue]);

  const renderColumns = (rowNbr: number) =>
    includedColumns?.map((column: any, colNbr: number) => {
      const standardProps = {
        key: column.name,
        name: `${name}[${rowNbr}].${column.name}`,
        label: smDown ? column.label: "",
        // Add class to reduce padding when field have no label.
        className: smDown ? '' : 'control-no-label',
        disabled: rowNbr === 0 && colNbr === 0 && rowCountMode === 2 ? true : readOnly,
        fullWidth: !!column.width || column.fullWidth || false,
        rowNameRef: `${name}[${rowNbr}]`,
        onChange: column.clearErrorsForOtherField
          ? (_:any, value: any) => {
              if (value) clearErrors(`${name}[${rowNbr}].${column.clearErrorsForOtherField}`);
            }
          : undefined,
      };

      const Component = column?.component;

      const key = `${name}[${rowNbr}].${column.name}`;
      const cellStyle = { width: typeof column.width === "number" ? `${column.width}px` : column.width };

      const cellProps = { key, style: cellStyle}

      switch (column.type) {
        case 'select':
          return (
            <TableCell {...cellProps}>
              <CasRHFAutocomplete {...standardProps} options={column.options} />
            </TableCell>
          );

        case 'text':
          return (
            <TableCell {...cellProps}>
              <CasRHFTextField {...standardProps} {...(column?.componentProps ?? {})} />
            </TableCell>
          );

        case 'multilineText':
          return (
            <TableCell {...cellProps}>
              <CasRHFMultilineTextField
                {...standardProps}
                minRows={1}
                {...(column?.componentProps ?? {})}
              />
            </TableCell>
          );

        case 'component':
          return (
            <TableCell {...cellProps}>
              <Component {...standardProps} {...(column?.componentProps ?? {})} />
            </TableCell>
          );

        case 'display':
          return (
            <TableCell {...cellProps}>
              <div className='control-no-margin'>
                <CasDisplayField
                  label={standardProps.label}
                  className={standardProps.className}
                  value={
                    <Box textAlign={smDown ? 'inherit' : column.align ?? 'center'}>
                      {typeof column.value === 'function'
                        ? column.value({ rowNameRef: standardProps.rowNameRef })
                        : column.value}
                    </Box>
                  }
                  {...(column?.componentProps ?? {})}
                />
              </div>
            </TableCell>
          );

        case 'cells':
          /* 
            For 'cells', the passed component can return multiple TableCells.
            So the component itself should handle label visibility (e.g., AmountPerTradeOrPercent),
            because CasMultiRowTable doesn't control labels in this case.
          */
          return <Component {...standardProps} />;

        default:
          throw new Error('Unsupported column type');
      }
    });

  const handleRemoveRow = (index: number) => {
    if (onRowDelete) onRowDelete(index);
    setIsEmptyRowAdded(true);
    remove(index);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ fieldState: { error } }) => (
        <>
          {error?.message && (
            <FormHelperText error={!!error} sx={{ mx: 0 }}>
              {error?.message}
            </FormHelperText>
          )}
          {/* Multi Row table code */}
          <Stack className={`CasMultiRowTable ${smDown && 'multiRowTableCard'}`} alignItems={alignItems}>
            <Table style={{ width }}>
              <TableHead>
                {title && (
                  <TableRow className="title">
                    <TableCell colSpan={fields.length}>{title}</TableCell>
                  </TableRow>
                )}
                {!smDown && showColumnHeaders &&  (
                  <TableRow className="table-header-row">
                    {includedColumns.map((column) => (
                      <TableCell key={column.label} align={column.align ?? 'inherit'}>
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </TableHead>
              <TableBody>
                {fields.map((field, index) => (
                  <TableRow key={field.id} sx={{ verticalAlign: 'super' }}>
                    {smDown ? <TableCell>{renderColumns(index)}</TableCell> : renderColumns(index)}
                    <TableCell key={`${name}[${index}].icons`}>
                      {!fixedRows &&
                        ((rowCountMode === 2 && index !== 0 && fields?.length > 2) ||
                          rowCountMode === 0 ||
                          rowCountMode === 1) && (
                        <IconButton
                          disabled={readOnly}
                          className="delete"
                          sx={{ opacity: 0.48, '&:hover': { opacity: 1 } }}
                          onClick={() => handleRemoveRow(index)}
                          color="error"
                        >
                          <Iconify icon="zondicons:minus-outline" width="25" height="25" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {totalColumns?.length && (
                <CasMultiRowTotalsRow
                  name={name}
                  columns={includedColumns}
                  totalColumns={totalColumns}
                />
              )}
            </Table>
            {!fixedRows && (
              <CasIconButton
                icon="gridicons:add-outline"
                onClick={() => append(emptyRow)}
                disabled={readOnly}
                color="primary"
              />
            )}
          </Stack>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </>
      )}
    />
  );
};

export default CasMultiRowTable;
